import React, { useState, useEffect } from 'react';
import { pricingAPI } from '../services/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Textarea } from '../components/ui/textarea';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Badge } from '../components/ui/badge';
import { Plus, Search, Edit, Trash2, DollarSign, TrendingUp, TrendingDown, Filter } from 'lucide-react';
import { toast } from 'sonner';

const categories = [
  'GENERAL - SITE MANAGEMENT',
  'DEMOLITION',
  'ELECTRICIAN - NORMAL HOURS',
  'PLASTERER - NORMAL HOURS',
  'CARPENTER - NORMAL HOURS',
  'PAINTER - NORMAL HOURS',
  'LABOUR',
  'TILER',
  'OTHER'
];

const units = [
  'Hrs', 'M2', 'Lm', 'Item', 'Each', 'day', 'sqm', 'sqft', 'lm', 'kg', 'lot'
];

export const PricingPage = () => {
  const [pricing, setPricing] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [formData, setFormData] = useState({
    code: '',
    name: '',
    description: '',
    unit: '',
    buy_rate: '',
    sell_rate: '',
    category: '',
    subcategory: '',
  });

  useEffect(() => {
    fetchPricing();
  }, []);

  const fetchPricing = async () => {
    try {
      const response = await pricingAPI.getAll();
      setPricing(response.data);
    } catch (error) {
      toast.error('Failed to fetch pricing');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const data = {
        ...formData,
        buy_rate: parseFloat(formData.buy_rate) || 0,
        sell_rate: parseFloat(formData.sell_rate) || 0,
        unit_price: parseFloat(formData.sell_rate) || 0, // Legacy support
      };

      if (editingItem) {
        await pricingAPI.update(editingItem.id, data);
        toast.success('Pricing updated');
      } else {
        await pricingAPI.create(data);
        toast.success('Pricing created');
      }
      
      fetchPricing();
      resetForm();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Operation failed');
    }
  };

  const handleEdit = (item) => {
    setEditingItem(item);
    setFormData({
      code: item.code || '',
      name: item.name,
      description: item.description || '',
      unit: item.unit,
      buy_rate: (item.buy_rate || 0).toString(),
      sell_rate: (item.sell_rate || item.unit_price || 0).toString(),
      category: item.category || '',
      subcategory: item.subcategory || '',
    });
    setDialogOpen(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this pricing item?')) return;
    try {
      await pricingAPI.delete(id);
      toast.success('Pricing deleted');
      fetchPricing();
    } catch (error) {
      toast.error('Failed to delete pricing');
    }
  };

  const resetForm = () => {
    setDialogOpen(false);
    setEditingItem(null);
    setFormData({
      code: '',
      name: '',
      description: '',
      unit: '',
      buy_rate: '',
      sell_rate: '',
      category: '',
      subcategory: '',
    });
  };

  const filteredPricing = pricing.filter(item => {
    const matchesSearch = item.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      item.description?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      item.code?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesCategory = categoryFilter === 'all' || item.category === categoryFilter;
    return matchesSearch && matchesCategory;
  });

  // Group pricing by category
  const groupedPricing = filteredPricing.reduce((acc, item) => {
    const cat = item.category || 'Other';
    if (!acc[cat]) acc[cat] = [];
    acc[cat].push(item);
    return acc;
  }, {});

  // Calculate margin
  const calcMargin = (buy, sell) => {
    if (!buy || !sell || buy === 0) return 0;
    return ((sell - buy) / buy * 100).toFixed(1);
  };

  // Get unique categories from data
  const uniqueCategories = [...new Set(pricing.map(p => p.category).filter(Boolean))];

  return (
    <div className="space-y-6 animate-fade-in" data-testid="pricing-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold">Schedule of Rates</h1>
          <p className="text-muted-foreground mt-1">Manage buy and sell rates for all services</p>
        </div>
        <Dialog open={dialogOpen} onOpenChange={(open) => { if (!open) resetForm(); else setDialogOpen(true); }}>
          <DialogTrigger asChild>
            <Button className="font-semibold rounded-lg" data-testid="add-pricing-btn">
              <Plus className="h-4 w-4 mr-2" />
              Add Rate
            </Button>
          </DialogTrigger>
          <DialogContent className="bg-card border-border max-w-lg">
            <DialogHeader>
              <DialogTitle>{editingItem ? 'Edit Rate' : 'Add New Rate'}</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="grid grid-cols-4 gap-4">
                <div className="space-y-2">
                  <Label>Code</Label>
                  <Input
                    value={formData.code}
                    onChange={(e) => setFormData({ ...formData, code: e.target.value })}
                    placeholder="1.01"
                    className="bg-input border-border rounded-lg"
                  />
                </div>
                <div className="col-span-3 space-y-2">
                  <Label>Item Name *</Label>
                  <Input
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    required
                    placeholder="e.g., Site Supervisor"
                    className="bg-input border-border rounded-lg"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label>Description</Label>
                <Textarea
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  className="bg-input border-border rounded-lg"
                  rows={2}
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Category *</Label>
                  <Select value={formData.category} onValueChange={(v) => setFormData({ ...formData, category: v })}>
                    <SelectTrigger className="bg-input border-border rounded-lg">
                      <SelectValue placeholder="Select category" />
                    </SelectTrigger>
                    <SelectContent className="bg-card border-border">
                      {categories.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Unit *</Label>
                  <Select value={formData.unit} onValueChange={(v) => setFormData({ ...formData, unit: v })}>
                    <SelectTrigger className="bg-input border-border rounded-lg">
                      <SelectValue placeholder="Select unit" />
                    </SelectTrigger>
                    <SelectContent className="bg-card border-border">
                      {units.map(u => <SelectItem key={u} value={u}>{u}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label className="flex items-center gap-2">
                    <TrendingDown className="h-4 w-4 text-red-500" />
                    Buy Rate ($) *
                  </Label>
                  <Input
                    type="number"
                    step="0.01"
                    value={formData.buy_rate}
                    onChange={(e) => setFormData({ ...formData, buy_rate: e.target.value })}
                    required
                    placeholder="0.00"
                    className="bg-input border-border rounded-lg"
                  />
                </div>
                <div className="space-y-2">
                  <Label className="flex items-center gap-2">
                    <TrendingUp className="h-4 w-4 text-green-500" />
                    Sell Rate ($) *
                  </Label>
                  <Input
                    type="number"
                    step="0.01"
                    value={formData.sell_rate}
                    onChange={(e) => setFormData({ ...formData, sell_rate: e.target.value })}
                    required
                    placeholder="0.00"
                    className="bg-input border-border rounded-lg"
                  />
                </div>
              </div>

              {formData.buy_rate && formData.sell_rate && (
                <div className="p-3 bg-muted rounded-lg text-center">
                  <span className="text-sm text-muted-foreground">Margin: </span>
                  <span className={`font-bold ${parseFloat(calcMargin(parseFloat(formData.buy_rate), parseFloat(formData.sell_rate))) > 0 ? 'text-green-500' : 'text-red-500'}`}>
                    {calcMargin(parseFloat(formData.buy_rate), parseFloat(formData.sell_rate))}%
                  </span>
                </div>
              )}

              <div className="flex justify-end gap-2">
                <Button type="button" variant="outline" onClick={resetForm} className="rounded-lg">Cancel</Button>
                <Button type="submit" className="font-semibold rounded-lg">
                  {editingItem ? 'Update' : 'Create'}
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-4">
        <div className="relative flex-1 max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Search by code, name or description..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-10 bg-input border-border rounded-lg"
          />
        </div>
        <Select value={categoryFilter} onValueChange={setCategoryFilter}>
          <SelectTrigger className="w-full sm:w-64 bg-input border-border rounded-lg">
            <Filter className="h-4 w-4 mr-2" />
            <SelectValue placeholder="Filter by category" />
          </SelectTrigger>
          <SelectContent className="bg-card border-border">
            <SelectItem value="all">All Categories</SelectItem>
            {uniqueCategories.map(cat => <SelectItem key={cat} value={cat}>{cat}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card className="bg-card border-border">
          <CardContent className="p-4">
            <div className="text-2xl font-bold">{pricing.length}</div>
            <div className="text-sm text-muted-foreground">Total Items</div>
          </CardContent>
        </Card>
        <Card className="bg-card border-border">
          <CardContent className="p-4">
            <div className="text-2xl font-bold">{uniqueCategories.length}</div>
            <div className="text-sm text-muted-foreground">Categories</div>
          </CardContent>
        </Card>
        <Card className="bg-card border-border">
          <CardContent className="p-4">
            <div className="text-2xl font-bold text-red-500">
              ${pricing.reduce((sum, p) => sum + (p.buy_rate || 0), 0).toLocaleString()}
            </div>
            <div className="text-sm text-muted-foreground">Total Buy</div>
          </CardContent>
        </Card>
        <Card className="bg-card border-border">
          <CardContent className="p-4">
            <div className="text-2xl font-bold text-green-500">
              ${pricing.reduce((sum, p) => sum + (p.sell_rate || p.unit_price || 0), 0).toLocaleString()}
            </div>
            <div className="text-sm text-muted-foreground">Total Sell</div>
          </CardContent>
        </Card>
      </div>

      {/* Pricing Table by Category */}
      {loading ? (
        <div className="text-center py-8 text-muted-foreground">Loading...</div>
      ) : Object.keys(groupedPricing).length === 0 ? (
        <Card className="bg-card border-border">
          <CardContent className="py-12 text-center">
            <DollarSign className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
            <h3 className="text-lg font-medium">No pricing items found</h3>
            <p className="text-muted-foreground">Add your first rate to get started</p>
          </CardContent>
        </Card>
      ) : (
        Object.entries(groupedPricing).sort().map(([category, items]) => (
          <Card key={category} className="bg-card border-border overflow-hidden">
            <CardHeader className="py-3 bg-muted/50">
              <CardTitle className="text-lg flex items-center justify-between">
                <span>{category}</span>
                <Badge variant="outline">{items.length} items</Badge>
              </CardTitle>
            </CardHeader>
            <CardContent className="p-0">
              <Table>
                <TableHeader>
                  <TableRow className="border-border">
                    <TableHead className="w-16">Code</TableHead>
                    <TableHead>Name</TableHead>
                    <TableHead className="w-20">Unit</TableHead>
                    <TableHead className="w-24 text-right">Buy $</TableHead>
                    <TableHead className="w-24 text-right">Sell $</TableHead>
                    <TableHead className="w-20 text-right">Margin</TableHead>
                    <TableHead className="w-24 text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {items.sort((a, b) => (a.code || '').localeCompare(b.code || '')).map(item => {
                    const margin = calcMargin(item.buy_rate, item.sell_rate || item.unit_price);
                    return (
                      <TableRow key={item.id} className="border-border">
                        <TableCell className="font-mono text-muted-foreground">{item.code || '-'}</TableCell>
                        <TableCell>
                          <div className="font-medium">{item.name}</div>
                          {item.description && (
                            <div className="text-xs text-muted-foreground truncate max-w-xs">{item.description}</div>
                          )}
                        </TableCell>
                        <TableCell>
                          <Badge variant="outline" className="font-normal">{item.unit}</Badge>
                        </TableCell>
                        <TableCell className="text-right font-mono text-red-500">
                          ${(item.buy_rate || 0).toFixed(2)}
                        </TableCell>
                        <TableCell className="text-right font-mono text-green-500">
                          ${(item.sell_rate || item.unit_price || 0).toFixed(2)}
                        </TableCell>
                        <TableCell className="text-right">
                          <span className={`font-mono ${parseFloat(margin) > 0 ? 'text-green-500' : 'text-red-500'}`}>
                            {margin}%
                          </span>
                        </TableCell>
                        <TableCell className="text-right">
                          <div className="flex justify-end gap-1">
                            <Button size="icon" variant="ghost" className="h-8 w-8" onClick={() => handleEdit(item)}>
                              <Edit className="h-4 w-4" />
                            </Button>
                            <Button size="icon" variant="ghost" className="h-8 w-8 text-destructive" onClick={() => handleDelete(item.id)}>
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        ))
      )}
    </div>
  );
};

export default PricingPage;
